import React, { FC, JSX, useEffect, useRef, useState } from "react";
import { AccessReportsAdminLogo, ManageOptions } from "@app/access-reports/admin";
import { cloneDeep } from "lodash";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import DragAndDropFileUploader from "@app/shared/DragAndDropFileUploader";
import LoadingBox from "@app/shared/LoadingBox";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import AccessReportsService from "@app/access-reports/accessReportsService";
import { AccessReportsWizardLogo } from "@app/access-reports/shared/accessReportsWizardLogo";

interface ManageLogosProps {
  /**
   * Prevents the user from making modifications to the template
   */
  disableUserActions: boolean;
  /**
   * The list of options to be managed
   */
  logos: AccessReportsWizardLogo[];
  /**
   * Handles saving any modifications done to the list of options
   */
  onSaveLogosClick: (options: AccessReportsWizardLogo[], reloadPage: boolean) => void;
}

const ManageLogo: FC<ManageLogosProps> = (props: ManageLogosProps) => {
  const accessReportsService = useRef(new AccessReportsService()).current;
  const [loading, setLoading] = useState(false);
  const [logoBlobs, setLogoBlobs] = useState(new Map<number, Blob>());
  const [logos, setLogos] = useState<AccessReportsWizardLogo[]>([]);

  useEffect(() => {
    if (props.logos.length) {
      updateLogos();
      // note: was previously async/awaited, no return value
      getLogoBlobs();
    }
  }, []);

  useEffect(() => {
    updateLogos();
    // note: was previously async/awaited, no return value
    getLogoBlobs();
  }, [props.logos]);

  const buildAdditionalIndividualProperty = (logo: AccessReportsWizardLogo): JSX.Element => {
    const logoBlob: Blob = logoBlobs.get(logo.id);

    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <div className="mt-3">
              {!!logoBlob ? (
                <img className="access-reports-admin__manage-logo--preview" src={URL.createObjectURL(logoBlob)} />
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <QESCheckbox
              containerClassName="mt-3 mb-3"
              checked={logo.maintainAspectRatio}
              disabled={props.disableUserActions}
              label="Maintain Aspect Ratio"
              testId={`access-reports-admin__form--maintain-aspect-ratio-${logo.id}`}
              onChange={() => onCheckboxChange(logo.id, "maintainAspectRatio")}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <QESCheckbox
              containerClassName="mt-0 mb-3"
              checked={logo.stretchToFit}
              disabled={props.disableUserActions}
              label="Stretch to Fit"
              testId={`access-reports-admin__form--stretch-to-fit-${logo.id}`}
              onChange={() => onCheckboxChange(logo.id, "stretchToFit")}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  const getAdditionalProperties = (): JSX.Element => {
    return (
      <div className="row">
        <div className="col-12">
          <div id="access-reports-admin__cover-page--loading-box">
            <LoadingBox show={loading} />
          </div>

          <DragAndDropFileUploader
            apiKeyName="logoFile"
            title=""
            filetype=".jpeg,.png"
            onUpload={onUpload}
            small={true}
            testId="access-reports-admin__cover-page--file-upload"
          />
          <hr className="mt-0" />
        </div>
      </div>
    );
  }

  const updateLogos = (): void => {
    // retrieve the logo images
    if (props.logos.length) {
      // the direct options prop array is not modified
      // in case the user wishes to "cancel" any changes made
      setLogos(cloneDeep(props.logos));
    }
  }

  const getLogoBlobs = async (): Promise<void> => {
    const newLogoBlobs: Map<number, Blob> = new Map<number, Blob>();

    for (const logo of props.logos) {
      const logoBlob: Blob = await accessReportsService.getLogoById(logo.id);

      if (!!logoBlob) {
        newLogoBlobs.set(logo.id, logoBlob);
      }
    }

    setLogoBlobs(newLogoBlobs);
  }

  const onCheckboxChange = (optionId: number, key: keyof AccessReportsWizardLogo): void => {
    const newLogos: AccessReportsWizardLogo[] = cloneDeep(logos);
    const logo: AccessReportsWizardLogo = newLogos.find(x => x.id === optionId);

    if (!!logo) {
      logo[key] = !logo[key];

      setLogos(newLogos);
    }
  }

  const onSaveLogosClick = (logos: AccessReportsWizardLogo[]): void => {
    props.onSaveLogosClick(logos, true)
  }

  const onUpload = async (data: FormData): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      const newLogoBlobs: Map<number, Blob> = new Map(logoBlobs);
      const newLogos: AccessReportsWizardLogo[] = cloneDeep(logos);

      setLoading(true);

      try {
        const logoProperties: AccessReportsAdminLogo = await accessReportsService.uploadLogo(data);

        if (!!logoProperties) {
          const logoOption: AccessReportsWizardLogo = {
            defaultProducts: [],
            displayName: logoProperties.imageName,
            hiddenProducts: [],
            id: logoProperties.accessLogoId,
            logoPath: "",
            maintainAspectRatio: true,
            name: logoProperties.imageName,
            stretchToFit: true,
          }

          newLogos.push(logoOption);

          const logoBlob: Blob = await accessReportsService.getLogoById(logoOption.id);

          if (!!logoBlob) {
            newLogoBlobs.set(logoOption.id, logoBlob);
          }

          await props.onSaveLogosClick(newLogos, false);
        }

        resolve();
      } catch {
        reject();
      }

      setLoading(false);
      setLogoBlobs(newLogoBlobs);
      setLogos(newLogos);
    });
  }

  return (
    <React.Fragment>
      <ManageOptions
        buildAdditionalIndividualProperty={buildAdditionalIndividualProperty}
        additionalProperties={!props.disableUserActions && getAdditionalProperties()}
        disableAddingNewOptions={true}
        disableUserActions={props.disableUserActions}
        onSaveOptionsClick={onSaveLogosClick}
        options={logos}
        optionType={"logo"}
        savedOptions={props.logos}
      />
    </React.Fragment>
  );
}

export { ManageLogo };
